/**
 * Scroll Effects
 * 1. Lenis — smooth inertia scrolling
 * 2. GSAP ScrollTrigger — element reveal animations
 * 3. Staggered product card reveals
 * 4. Subtle parallax on hero / large images
 */

document.addEventListener('DOMContentLoaded', function () {

    /* Wait for both GSAP + Lenis to be ready */

    if (typeof gsap === 'undefined' || typeof Lenis === 'undefined') {
        setTimeout(initScrollEffects, 200);
    } else {
        initScrollEffects();
    }
});

function initScrollEffects() {
    if (typeof gsap === 'undefined' || typeof Lenis === 'undefined') {
        console.warn('[scroll-effects] GSAP or Lenis not loaded. Skipping scroll effects.');
        return;
    }

    /* 1. LENIS — smooth inertia scrolling */

    const lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        direction: 'vertical',
        gestureDirection: 'vertical',
        smooth: true,
        mouseMultiplier: 1.0,
        touchMultiplier: 2,
        infinite: false,
    });

    window.lenis = lenis;


    gsap.registerPlugin(ScrollTrigger);
    lenis.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
        lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);


    ScrollTrigger.scrollerProxy(document.documentElement, {
        scrollTop(value) {
            if (arguments.length) {
                lenis.scrollTo(value, { immediate: true });
            }
            return lenis.scroll;
        },
        getBoundingClientRect() {
            return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
        },
    });


    /* 2. REVEAL ANIMATIONS — fade-up on scroll */


    const fadeTargets = [
        '.sale-selection-heading',
        '.sale-selection-sub',
        '.sale-selection-tags',
        '.sale-selection-search',
        '.product-catalogue__filters',
        '.advice-section h2, .advice-section p',
        '.advice-section .hora-button-1',
        '.advice-section .hora-button-1-inverted',
        '.footer-section h3',
        '.footer-column',
        '.product-detail__heading',
        '.product-detail__price-row',
        '.product-detail__description',
    ];

    fadeTargets.forEach((selector) => {
        const els = document.querySelectorAll(selector);
        if (!els.length) return;

        els.forEach((el) => {
            gsap.fromTo(el,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.9,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: el,
                        start: 'top 88%',
                        toggleActions: 'play none none none',
                        once: true,
                    },
                }
            );
        });
    });


    /* 3. STAGGERED PRODUCT CARD REVEALS */

    const grid = document.querySelector('.product-catalogue__grid');
    if (grid) {
        const cards = grid.querySelectorAll('.product-catalogue__item');


        const rows = {};
        cards.forEach((card) => {
            const top = Math.round(card.getBoundingClientRect().top + window.scrollY);
            if (!rows[top]) rows[top] = [];
            rows[top].push(card);
        });

        Object.values(rows).forEach((rowCards) => {
            gsap.fromTo(rowCards,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.75,
                    ease: 'power3.out',
                    stagger: 0.12,
                    scrollTrigger: {
                        trigger: rowCards[0],
                        start: 'top 90%',
                        toggleActions: 'play none none none',
                        once: true,
                    },
                }
            );
        });
    }


    /* 4. HERO / SALE-SELECTION SECTION — subtle parallax */

    const heroSection = document.querySelector('.sale-selection');
    if (heroSection) {
        const heroImg = heroSection.querySelector('img, .sale-selection-image');
        if (heroImg) {
            gsap.to(heroImg, {
                yPercent: 20,
                ease: 'none',
                scrollTrigger: {
                    trigger: heroSection,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 1.5,
                },
            });
        }
    }


    /* 5. SECTION HEADINGS — clip-path reveal */

    const bigHeadings = document.querySelectorAll(
        'h1, h2, .section-title, .product-card__name'
    );

    bigHeadings.forEach((el) => {
        if (el.closest('.product-catalogue__grid')) return;

        gsap.fromTo(el,
            { opacity: 0, y: 30 },
            {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: el,
                    start: 'top 92%',
                    toggleActions: 'play none none none',
                    once: true,
                },
            }
        );
    });


    /* 6. ADVICE SECTION IMAGE — parallax */

    const adviceImgs = document.querySelectorAll('.advice-section img, .advice-section-image');
    adviceImgs.forEach(function (adviceImg) {
        gsap.fromTo(adviceImg, {
            yPercent: -15
        }, {
            yPercent: 15,
            ease: 'none',
            scrollTrigger: {
                trigger: adviceImg.parentElement,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1.5,
            },
        });
    });



    /* 7. PRODUCT DESCRIPTION — differential parallax */
    const mm = gsap.matchMedia();

    // Product Description: Desktop (>= 900px)
    mm.add("(min-width: 900px)", () => {
        // 7a. Product Description 1 & 2 (Two Columns)
        const desc12 = document.querySelectorAll('.product-description-1, .product-description-2');
        desc12.forEach((section) => {
            const left = section.querySelector('[class$="__left"]');
            const right = section.querySelector('[class$="__right"]');
            if (!left || !right) return;

            const leftImgWrap = left.querySelector('[class*="__small-image"], [class*="__large-image"]');
            const rightImgWrap = right.querySelector('[class*="__small-image"], [class*="__large-image"]');

            /* Left column image scrolls UP faster (negative y) */
            if (leftImgWrap) {
                gsap.to(leftImgWrap, {
                    y: '-5rem',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: section,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1,
                    },
                });
            }

            /* Right column image scrolls DOWN slower (positive y) */
            if (rightImgWrap) {
                gsap.to(rightImgWrap, {
                    y: '5rem',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: section,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1,
                    },
                });
            }
        });

        // 7b. Product Description 3 (Three Images Grid)
        const desc3 = document.querySelectorAll('.product-description-3');
        desc3.forEach((section) => {
            const rows = section.querySelectorAll('.product-description-3__row');
            rows.forEach((row) => {
                const step0 = row.querySelector('.product-description-3__step-0');
                const step1 = row.querySelector('.product-description-3__step-1');
                const step2 = row.querySelector('.product-description-3__step-2');

                // Apply wrapper Y shifts (Staggered cascading effect)
                if (step0) gsap.to(step0, { y: '-4rem', ease: 'none', scrollTrigger: { trigger: row, start: 'top bottom', end: 'bottom top', scrub: 1 } });
                if (step1) gsap.to(step1, { y: '2rem', ease: 'none', scrollTrigger: { trigger: row, start: 'top bottom', end: 'bottom top', scrub: 1 } });
                if (step2) gsap.to(step2, { y: '6rem', ease: 'none', scrollTrigger: { trigger: row, start: 'top bottom', end: 'bottom top', scrub: 1 } });
            });
        });
    });

    // Product Description: Mobile & Tablet (< 900px)
    mm.add("(max-width: 899px)", () => {
        // 7c. Mobile Product Description 1 & 2 Parallax (position-based)
        const desc12 = document.querySelectorAll('.product-description-1, .product-description-2');
        desc12.forEach((section) => {
            const left = section.querySelector('[class$="__left"]');
            const right = section.querySelector('[class$="__right"]');
            if (!left && !right) return;

            const leftImgWrap = left ? left.querySelector('[class*="__small-image"], [class*="__large-image"]') : null;
            const rightImgWrap = right ? right.querySelector('[class*="__small-image"], [class*="__large-image"]') : null;

            /* Left column image scrolls UP faster (negative y) */
            if (leftImgWrap) {
                gsap.to(leftImgWrap, {
                    y: '-3rem',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: section,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1.5,
                    },
                });
            }

            /* Right column image scrolls DOWN slower (positive y) */
            // if (rightImgWrap) {
            //     gsap.to(rightImgWrap, {
            //         y: '-1.5rem',
            //         ease: 'none',
            //         scrollTrigger: {
            //             trigger: section,
            //             start: 'top bottom',
            //             end: 'bottom top',
            //             scrub: 1.5,
            //         },
            //     });
            // }
        });

        // 7d. Mobile Product Description 3 Parallax (position-based)
        const desc3 = document.querySelectorAll('.product-description-3');
        desc3.forEach((section) => {
            const rows = section.querySelectorAll('.product-description-3__row');
            rows.forEach((row) => {
                const step0 = row.querySelector('.product-description-3__step-0');
                const step1 = row.querySelector('.product-description-3__step-1');
                const step2 = row.querySelector('.product-description-3__step-2');

                // Apply wrapper Y shifts (Staggered cascading effect)
                if (step0) gsap.to(step0, { y: '-4rem', ease: 'none', scrollTrigger: { trigger: row, start: 'top bottom', end: 'bottom top', scrub: 1.5 } });
                if (step1) gsap.to(step1, { y: '1.5rem', ease: 'none', scrollTrigger: { trigger: row, start: 'top bottom', end: 'bottom top', scrub: 1.5 } });
                if (step2) gsap.to(step2, { y: '-4rem', ease: 'none', scrollTrigger: { trigger: row, start: 'top bottom', end: 'bottom top', scrub: 1.5 } });
            });
        });
    });

    /* 8. BLOG CARDS — image parallax */

    /*
     * Scope image lookups to the desktop grid.
     *
     * When the "Desktop Layout for Blog Cards on Mobile" setting is on,
     * blog-card.liquid renders a second, mobile-only copy of the images BEFORE
     * the desktop grid, reusing the same class names
     * (.blog-card__small-image / __large-image / __tall-image).
     *
     * card.querySelector() returns the first match in DOM order, which is that
     * mobile copy — and it is display:none above 768px. The result was GSAP
     * animating hidden elements while the visible desktop images never moved.
     * Querying inside .blog-card__grid removes the ambiguity.
     */
    function blogCardScope(card) {
        return card.querySelector('.blog-card__grid') || card;
    }

    // Blog cards: Desktop (>= 900px)
    mm.add("(min-width: 900px)", () => {
        const blogCards = document.querySelectorAll('.blog-card');
        blogCards.forEach((card) => {
            const isA = card.classList.contains('blog-card--type-a');
            const isB = card.classList.contains('blog-card--type-b');
            const isC = card.classList.contains('blog-card--type-c');

            const scope = blogCardScope(card);
            let leftWrap = null;
            let rightWrap = null;

            if (isA) {
                leftWrap = scope.querySelector('.blog-card__small-image');
                rightWrap = scope.querySelector('.blog-card__large-image');
            } else if (isC) {
                leftWrap = scope.querySelector('.blog-card__large-image');
                rightWrap = scope.querySelector('.blog-card__small-image');
            } else if (isB) {
                const tallImages = scope.querySelectorAll('.blog-card__tall-image');
                if (tallImages.length >= 2) {
                    leftWrap = tallImages[0];
                    rightWrap = tallImages[1];
                }
            }

            if (leftWrap) {
                // Left container moves UP fast
                gsap.fromTo(leftWrap,
                    { y: 60 },
                    {
                        y: -60,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: card,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: 1.5,
                        }
                    }
                );
            }

            if (rightWrap) {
                // Right container moves DOWN slower
                gsap.fromTo(rightWrap,
                    { y: -30 },
                    {
                        y: 30,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: card,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: 1.5,
                        }
                    }
                );
            }
        });
    });

    // Blog cards: Mobile & Tablet (< 900px)
    mm.add("(max-width: 899px)", () => {
        const blogCards = document.querySelectorAll('.blog-card');
        blogCards.forEach((card) => {
            // Skip standard inner parallax if it's flagged and on a mobile screen width
            const isFlaggedMobile = card.classList.contains('blog-card--desktop-layout-mobile') && window.innerWidth <= 768;
            if (isFlaggedMobile) {
                return;
            }

            const isA = card.classList.contains('blog-card--type-a');
            const isB = card.classList.contains('blog-card--type-b');
            const isC = card.classList.contains('blog-card--type-c');

            // Same scoping fix as the desktop block above: between 769px and
            // 899px the desktop grid is still the visible one, so the mobile
            // copy of the images must not be picked up here either.
            const scope = blogCardScope(card);
            let leftWrap = null;
            let rightWrap = null;

            if (isA) {
                leftWrap = scope.querySelector('.blog-card__small-image');
                rightWrap = scope.querySelector('.blog-card__large-image');
            } else if (isC) {
                leftWrap = scope.querySelector('.blog-card__large-image');
                rightWrap = scope.querySelector('.blog-card__small-image');
            } else if (isB) {
                const tallImages = scope.querySelectorAll('.blog-card__tall-image');
                if (tallImages.length >= 2) {
                    leftWrap = tallImages[0];
                    rightWrap = tallImages[1];
                }
            }

            if (leftWrap) {
                const leftImg = leftWrap.querySelector('img');
                if (leftImg) {
                    // Reset container translation
                    gsap.set(leftWrap, { y: 0 });
                    // Left image moves UP inside its wrapper
                    gsap.fromTo(leftImg,
                        { yPercent: 12, scale: 1.2 },
                        {
                            yPercent: -12,
                            scale: 1.2,
                            ease: 'none',
                            scrollTrigger: {
                                trigger: card,
                                start: 'top bottom',
                                end: 'bottom top',
                                scrub: 1.5,
                            }
                        }
                    );
                }
            }

            if (rightWrap) {
                const rightImg = rightWrap.querySelector('img');
                if (rightImg) {
                    // Reset container translation
                    gsap.set(rightWrap, { y: 0 });
                    // Right image moves UP inside its wrapper (same direction as left image)
                    gsap.fromTo(rightImg,
                        { yPercent: 12, scale: 1.2 },
                        {
                            yPercent: -12,
                            scale: 1.2,
                            ease: 'none',
                            scrollTrigger: {
                                trigger: card,
                                start: 'top bottom',
                                end: 'bottom top',
                                scrub: 1.5,
                            }
                        }
                    );
                }
            }
        });
    });

    // Blog cards: Flagged Mobile (< 769px)
    mm.add("(max-width: 768px)", () => {
        const flaggedCards = document.querySelectorAll('.blog-card.blog-card--desktop-layout-mobile');
        flaggedCards.forEach((card) => {
            const leftWrap = card.querySelector('.blog-card__mobile-image-left');
            const rightWrap = card.querySelector('.blog-card__mobile-image-right');

            if (leftWrap) {
                gsap.to(leftWrap, {
                    y: '-5rem',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1.5,
                    }
                });
            }

            if (rightWrap) {
                gsap.to(rightWrap, {
                    y: '-1.5rem',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: card,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1.5,
                    }
                });
            }
        });
    });
    /* 11. IMAGE SANDWICH TEXT ANIMATIONS */
    const sandwichTitles = Array.from(document.querySelectorAll('.image-sandwich__title'))
        .filter(el => !el.closest('.lhs') && !el.closest('.article-split-section__sticky') && !el.closest('[class*="sticky"]') && !el.closest('.article-full-section'));
    const sandwichTaglines = Array.from(document.querySelectorAll('.image-sandwich__tagline'))
        .filter(el => !el.closest('.lhs') && !el.closest('.article-split-section__sticky') && !el.closest('[class*="sticky"]') && !el.closest('.article-full-section'));

    const sandwichTitlesArticleSection = Array.from(document.querySelectorAll('.image-sandwich__title'))
        .filter(el => !el.closest('.lhs') && el.closest('.article-full-section'));
    const sandwichTaglinesArticleSection = Array.from(document.querySelectorAll('.image-sandwich__tagline'))
        .filter(el => !el.closest('.lhs') && el.closest('.article-full-section'));

    const isMobile = window.innerWidth <= 768;

    sandwichTitles.forEach((title) => {
        gsap.set(title, { x: -300 });
        gsap.to(title, {
            x: -10,
            duration: 6,
            ease: 'expo.out',
            scrollTrigger: {
                trigger: title,
                start: 'top 95%',
                toggleActions: 'play none none none',
                once: true,
            }
        });
    });

    sandwichTaglines.forEach((tagline) => {
        gsap.set(tagline, { x: 300 });
        gsap.to(tagline, {
            x: 10,
            duration: 6,
            ease: 'expo.out',
            scrollTrigger: {
                trigger: tagline,
                start: 'top 95%',
                toggleActions: 'play none none none',
                once: true,
            }
        });
    });

    sandwichTitlesArticleSection.forEach((title) => {
        gsap.set(title, { x: isMobile ? -15 : -100 });
        gsap.to(title, {
            x: isMobile ? 0 : 0,
            duration: 6,
            ease: 'expo.out',
            scrollTrigger: {
                trigger: title,
                start: 'top 95%',
                toggleActions: 'play none none none',
                once: true,
            }
        });
    });

    sandwichTaglinesArticleSection.forEach((tagline) => {
        gsap.set(tagline, { x: isMobile ? 15 : 100 });
        gsap.to(tagline, {
            x: isMobile ? 0 : 0,
            duration: 6,
            ease: 'expo.out',
            scrollTrigger: {
                trigger: tagline,
                start: 'top 95%',
                toggleActions: 'play none none none',
                once: true,
            }
        });
    });

    /* 12. ARTICLE GALLERY — image parallax */
    // Article Gallery: Desktop (> 1200px)
    mm.add("(min-width: 1201px)", () => {
        const articleGalleries = document.querySelectorAll('.article-gallery');
        articleGalleries.forEach((gallery) => {
            const leftWrap = gallery.querySelector('.article-gallery__text-col .article-gallery__img-wrap');
            const rightWrap = gallery.querySelector('.article-gallery__image-col .article-gallery__img-wrap');

            if (leftWrap) {
                // Left container moves UP fast
                gsap.fromTo(leftWrap,
                    { y: 60 },
                    {
                        y: -60,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: gallery,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: 1.5,
                        }
                    }
                );
            }

            if (rightWrap) {
                // Right container moves DOWN slower
                gsap.fromTo(rightWrap,
                    { y: -30 },
                    {
                        y: 30,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: gallery,
                            start: 'top bottom',
                            end: 'bottom top',
                            scrub: 1.5,
                        }
                    }
                );
            }
        });
    });

    // Article Gallery: Mobile & Tablet (<= 1200px)
    mm.add("(max-width: 1200px)", () => {
        const articleGalleries = document.querySelectorAll('.article-gallery');
        articleGalleries.forEach((gallery) => {
            const rightWrap = gallery.querySelector('.article-gallery__text-col .article-gallery__img-wrap');
            const leftWrap = gallery.querySelector('.article-gallery__image-col .article-gallery__img-wrap');

            if (leftWrap) {
                gsap.to(leftWrap, {
                    y: '-5rem',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: gallery,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1.5,
                    }
                });
            }

            if (rightWrap) {
                gsap.to(rightWrap, {
                    y: '-1.5rem',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: gallery,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1.5,
                    }
                });
            }
        });
    });

    ScrollTrigger.refresh();

    /* 8. FOOTER SCROLL-TO-NAVIGATE */
    initFooterScrollNav(lenis);

    /* 9. SCROLL INDICATOR (bottom-right) */
    initScrollIndicator();


    /* 10. HIDE LOGO NAME ON SCROLL */
    const logoName = document.querySelector('.site-header__logo-name');
    if (logoName) {
        gsap.to(logoName, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.out',
            scrollTrigger: {
                trigger: document.body,
                start: '100px top',
                toggleActions: 'play none none reverse'
            }
        });
    }
}


/** Scroll indicator — animated vertical bar, bottom-right of viewport. */
function initScrollIndicator() {
    const components = document.querySelectorAll('.scroller_component');
    if (!components.length) return;

    components.forEach((component) => {
        const scrollbar = component.querySelector('.scroller_scrollbar');
        const filler = component.querySelector('.scroller_filler');
        if (!scrollbar || !filler) return;

        const originalHeight = scrollbar.offsetHeight;
        const baseDuration = 3.0;


        gsap.set(filler, { yPercent: -100 });

        const scrollTween = gsap.to(filler, {
            yPercent: 200,
            duration: baseDuration,
            ease: 'power1.inOut',
            repeat: -1,
            onRepeat: () => gsap.set(filler, { yPercent: -100 })
        });


        component.addEventListener('mouseenter', () => {
            gsap.killTweensOf(filler);
            scrollTween.pause();

            gsap.to(scrollbar, {
                height: originalHeight * 1.5,
                duration: 0.3,
                ease: 'power1.out'
            });

            gsap.to(filler, {
                yPercent: 0,
                duration: 0.4,
                ease: 'power1.out'
            });
        });


        component.addEventListener('mouseleave', () => {
            gsap.killTweensOf(filler);

            gsap.to(scrollbar, {
                height: originalHeight,
                duration: 0.3,
                ease: 'power1.inOut'
            });

            gsap.to(filler, {
                yPercent: 200,
                duration: baseDuration / 2,
                ease: 'power1.inOut',
                onComplete: () => {
                    gsap.set(filler, { yPercent: -100 });
                    scrollTween.restart();
                }
            });
        });
    });
}



/** Footer scroll-to-navigate */
function initFooterScrollNav(lenis) {
    const container = document.querySelector('.footer_scrollbalk-container');
    const fill = document.querySelector('.footer_scrollbalk-bar-fill');
    const footer = document.querySelector('footer.footer_component');
    const logo = container ? container.querySelector('.move-to-center') : null;

    if (!container || !fill || !footer) {
        console.warn('[footer-nav] Missing elements — aborting.');
        return;
    }

    /* Resolve target URL */
    let nextUrl = (container.dataset.nextUrl || '').trim();

    if (!nextUrl) {

        const isProductPage = window.location.pathname.includes('/products/');
        nextUrl = isProductPage ? '/' : 'https://www.horabarneveld.nl/';
        console.warn('[footer-nav] data-next-url missing — using fallback:', nextUrl);
    } else {
        console.log('[footer-nav] Target URL:', nextUrl);
    }

    let navigating = false;

    const OVERSCROLL_PX = 120;

    const FILL_THRESHOLD = 0.8;

    function getScrollBounds() {
        const docH = document.documentElement.scrollHeight;
        const winH = window.innerHeight;
        const maxNaturalScroll = docH - winH;
        // Start filling only when the main footer content scrolls past the bottom of the viewport
        const fillStart = footer.offsetTop + footer.offsetHeight - winH;
        return { fillStart, maxNaturalScroll };
    }

    function updateProgress() {
        if (navigating) return;
        const scrollY = window.scrollY;
        const { fillStart, maxNaturalScroll } = getScrollBounds();

        const normalRange = maxNaturalScroll - fillStart;
        const normalScrolled = Math.min(scrollY - fillStart, normalRange);
        const normalProgress = Math.min(Math.max(normalScrolled / normalRange, 0), 1);

        const overscrollProgress = Math.min(Math.max(_overscrollPx / OVERSCROLL_PX, 0), 1);

        const progress = normalProgress < 1
            ? normalProgress * FILL_THRESHOLD
            : FILL_THRESHOLD + overscrollProgress * (1 - FILL_THRESHOLD);

        fill.style.width = (progress * 100) + '%';

        if (logo) {
            // Translate from +100px up to 0px natively instead of scaling
            const currentY = 100 * (1 - progress);
            logo.style.transform = `translateX(-50%) translateY(calc(-50% + ${currentY}px))`;
        }

        if (progress >= 0.999) {
            triggerNavigation();
        }
    }

    let _overscrollPx = 0;
    let _overscrollDecayTimer = null;

    function handleWheelOverscroll(e) {
        if (navigating) return;
        const { maxNaturalScroll } = getScrollBounds();
        const atBottom = window.scrollY >= maxNaturalScroll - 2;
        if (!atBottom) {
            _overscrollPx = 0;
            return;
        }
        if (e.deltaY > 0) {
            _overscrollPx = Math.min(_overscrollPx + e.deltaY * 0.5, OVERSCROLL_PX);
            updateProgress();
        }
        clearTimeout(_overscrollDecayTimer);
        _overscrollDecayTimer = setTimeout(() => {
            _overscrollPx = Math.max(_overscrollPx - 20, 0);
            updateProgress();
        }, 400);
    }

    let _touchStartY = 0;
    function handleTouchStart(e) { _touchStartY = e.touches[0].clientY; }
    function handleTouchMove(e) {
        if (navigating) return;
        const { maxNaturalScroll } = getScrollBounds();
        const atBottom = window.scrollY >= maxNaturalScroll - 2;
        if (!atBottom) { _overscrollPx = 0; return; }
        const deltaY = _touchStartY - e.touches[0].clientY; // positive = swipe up (scroll down)
        if (deltaY > 0) {
            _overscrollPx = Math.min(_overscrollPx + deltaY * 0.4, OVERSCROLL_PX);
            _touchStartY = e.touches[0].clientY;
            updateProgress();
        }
    }

    function triggerNavigation() {
        if (navigating) return;
        navigating = true;

        if (lenis) lenis.stop();
        document.body.style.overflow = 'hidden';

        const tl = gsap.timeline({
            onComplete: () => {
                window.location.href = nextUrl;
            }
        });

        const header = document.querySelector('.section_header, header, .nav-overlay');
        if (header) tl.to(header, { opacity: 0, duration: 0.35, ease: 'power2.in' }, 0);
        tl.to('.footer_scrollbalk', { opacity: 0, duration: 0.25, ease: 'power2.in' }, 0);
        tl.to('body', { opacity: 0, duration: 0.55, ease: 'power2.inOut' }, 0.15);
    }


    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('wheel', handleWheelOverscroll, { passive: true });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    if (lenis && typeof lenis.on === 'function') {
        lenis.on('scroll', updateProgress);
    }


    updateProgress();
}


